import ReadOnlyMessageText from './ReadOnlyMessageText';
import { decodeDisplayText } from '../../util/displayText';
import React from 'react';
import styled from 'styled-components';

import { PALE_WHITE_1, PALE_WHITE_6 } from '../../appearance/Colors';
import ReadOnlyUrlMetaPreview from './ReadOnlyUrlMetaPreview';
import ReadOnlyFilePreview from './ReadOnlyFilePreview';
import ReadOnlyMessageActions from './ReadOnlyMessageActions';
import ReadOnlyMessageImage from './ReadOnlyMessageImage';

const BODY_PREVIEW_LIMIT = 220;

const truncate = (text, limit) => (text && text.length > limit ? `${text.slice(0, limit).trim()}…` : text);

/**
 * Read-only message card — same visual concept as DefaultMessage's
 * MessageWrapper (border/background/radius tokens match), but a separate,
 * independent component. DefaultMessage/MessagesList require AuthContext
 * and several mutation hooks (editing, drag-drop, thread state) with no
 * anonymous/read-only equivalent, so this mirrors the look without
 * importing that component tree. Used by published folder/message pages
 * and intended for reuse by the "Shared with me" folder view.
 */
const ReadOnlyMessageCard = ({ message, onClick, onSaveMessage = null, saving = false, onActionStatus = null }) => {
    const urlMeta = message.urlMeta?.[0];
    // `message.title` is frequently auto-populated from the scraped page
    // title for link messages, not something the user typed — treating it
    // as "native content" hid the richer ReadOnlyUrlMetaPreview (publisher,
    // description, gallery, media) behind a bare title for any link/article
    // save. Only a genuine `body` (the user's own note) should suppress it;
    // matches how ReadOnlyMessageChat/DetailContent already show urlMeta
    // unconditionally whenever it exists.
    const hasNativeBody = Boolean(message.body);
    const bodyPreview = hasNativeBody ? truncate(decodeDisplayText(message.body), BODY_PREVIEW_LIMIT) : null;

    return (
        <CardShell>
            <ReadOnlyMessageActions
                message={message}
                onSaveMessage={onSaveMessage}
                saving={saving}
                onStatus={onActionStatus}
                placement="outside-card"
            />
            <Card onClick={onClick} $clickable={Boolean(onClick)}>
                {message.image ? <CardImage image={message.image} alt={message.title || ''} loading="lazy" /> : null}
                <CardBody>
                    <ReadOnlyFilePreview file={message.file} />

                    {!urlMeta && message.title ? <CardTitle><ReadOnlyMessageText text={message.title}/></CardTitle> : null}
                    {hasNativeBody ? <CardBodyText><ReadOnlyMessageText text={bodyPreview}/></CardBodyText> : null}

                    {urlMeta ? (
                        <ReadOnlyUrlMetaPreview
                            urlMeta={urlMeta}
                            sourceHost={message.sourceHost}
                            variant="full"
                            message={message}
                            index={0}
                        />
                    ) : !hasNativeBody ? (
                        <>
                            {bodyPreview ? <CardBodyText><ReadOnlyMessageText text={bodyPreview}/></CardBodyText> : null}
                            {message.sourceHost ? <CardSource>{message.sourceHost}</CardSource> : null}
                        </>
                    ) : null}
                </CardBody>
            </Card>
        </CardShell>
    );
};

const CardShell = styled.div`
    break-inside: avoid;
    margin-bottom: 12px;
    padding-right: 36px;
    position: relative;
    overflow: visible;
`;

const Card = styled.div`
    border: 1px solid ${PALE_WHITE_1};
    border-radius: 8px;
    overflow: hidden;
    background: rgba(35, 39, 42, 0.72);
    display: flex;
    flex-direction: column;
    position: relative;
    cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
    transition: border-color 0.15s ease;

    &:hover {
        border-color: ${({ $clickable }) => ($clickable ? 'rgba(255, 255, 255, 0.28)' : PALE_WHITE_1)};
    }
`;

const CardImage = styled(ReadOnlyMessageImage)`
    width: 100%;
    display: block;
`;

const CardBody = styled.div`
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const CardTitle = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
`;

const CardBodyText = styled.div`
    font-size: 14px;
    line-height: 1.4;
    color: rgba(255, 255, 255, 0.72);
`;

const CardSource = styled.div`
    font-size: 11px;
    color: ${PALE_WHITE_6};
    text-transform: uppercase;
    letter-spacing: 0.02em;
`;

export default ReadOnlyMessageCard;
